import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import {
  X,
  Upload,
  Search,
  Users,
  Plus,
  Phone,
  Building2,
  User,
  Loader2,
  FileSpreadsheet,
  AlertCircle,
  RotateCcw,
  Pencil,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose, DialogFooter } from '@/components/ui/dialog';
import { useRecipients } from '@/hooks/use-recipients';
import { extractCSVHeaders, autoDetectColumns, parseCSVLine } from '@/lib/utils/csv';
import { useSaaSModule } from '@/hooks/use-saas-module';
import { useModuleStore } from '@/stores/module-store';
import { ListPagePicker } from './list-page-picker';
import { useMessageStore } from '@/stores/message-store';
import { useUIStore } from '@/stores/ui-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';

import { applyCountryCode } from '@/lib/utils/phone';
import { MultiCountryWarning } from '@/components/common/multi-country-warning';
import type { Recipient } from '@/types/messaging';
import type { CRMField, CRMRecord as SaaSRecord } from '@/types/saas';

const SOURCE_ICONS: Record<string, typeof User> = {
  crm: Building2,
  manual: User,
  csv: FileSpreadsheet,
};

const SOURCE_COLORS: Record<string, string> = {
  crm: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30',
  manual: 'bg-violet-50 text-violet-600 border-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-500/30',
  csv: 'bg-green-50 text-green-600 border-green-200 dark:bg-green-500/10 dark:text-green-300 dark:border-green-500/30',
};

/** Tracks a loaded CSV/Excel sheet for multi-sheet import */
interface LoadedSheet {
  tag: string;       // "sheet-1", "sheet-2", ...
  fileName: string;  // e.g. "contacts.xlsx"
  csvText: string;   // parsed CSV content
}

const PHONE_KEYWORDS = ['phone', 'mobile', 'fax', 'tel'];

/** Same phone-field rule extractPhoneFields uses to build the pills. */
function isPhoneFieldDef(f: CRMField): boolean {
  return f.dataType === 'phone' || PHONE_KEYWORDS.some((kw) => f.displayLabel.toLowerCase().includes(kw));
}

/**
 * Resolve a raw record value into individual phone strings: handles
 * Pipedrive-style `[{value, label}]` contact arrays and comma-separated
 * lists — the same shapes extractPhoneFields resolves when building
 * the pills, so pill membership can't disagree with pill existence.
 */
function resolveRawPhoneValues(rawVal: unknown): string[] {
  if (rawVal == null) return [];
  if (Array.isArray(rawVal)) {
    return rawVal
      .map((item) =>
        item && typeof item === 'object'
          ? String((item as { value?: unknown }).value ?? '').trim()
          : String(item ?? '').trim()
      )
      .filter(Boolean);
  }
  const rawStr = String(rawVal).trim();
  if (!rawStr) return [];
  return rawStr.includes(',')
    ? rawStr.split(',').map((v) => v.trim()).filter(Boolean)
    : [rawStr];
}

/**
 * Every phone field a record added from the Search CRM dialog holds.
 * Unlike extractPhoneFields there is no value dedup across records: two
 * leads sharing a number — one under Mobile, one under Phone — must give
 * both pills. Schema phone fields when loaded, else the key-name keyword
 * scan addRecordsAsRecipients falls back to.
 */
function searchRecordPhoneFields(record: SaaSRecord, phoneFieldDefs: CRMField[]): Array<{ apiName: string; value: string }> {
  if (!record.data) return [];
  const apiNames = phoneFieldDefs.length > 0
    ? phoneFieldDefs.map((f) => f.apiName)
    : Object.keys(record.data).filter((key) => PHONE_KEYWORDS.some((kw) => key.toLowerCase().includes(kw)));
  const fields: Array<{ apiName: string; value: string }> = [];
  for (const apiName of apiNames) {
    for (const value of resolveRawPhoneValues(record.data[apiName])) {
      if (value.replace(/\D/g, '')) fields.push({ apiName, value });
    }
  }
  return fields;
}

export function RecipientPicker() {
  const {
    recipients,
    addManualRecipient,
    removeRecipient,
    removeRecipientsByTag,
    clearRecipients,
    importFromCSV,
    searchCRM,
    addFromCRM,
    count,
  } = useRecipients();

  const { module: saasModule } = useSaaSModule();
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const crmRecordsLoading = useModuleStore((s) => s.crmRecordsLoading);
  const crmRecordsTotal = useModuleStore((s) => s.crmRecordsTotal);
  const crmRecordsLoaded = useModuleStore((s) => s.crmRecordsLoaded);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const isPhoneBased = tpaModule?.isPhoneBasedRecipient?.() ?? true;
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const saasName = saasModule?.name || 'CRM';
  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);
  const defaultCountryCode = preferences.defaultCountryCode || '';

  const [input, setInput] = useState('');
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [crmSearchOpen, setCrmSearchOpen] = useState(false);
  /** Multi-sheet state: each uploaded CSV/Excel is tracked separately */
  const [loadedSheets, setLoadedSheets] = useState<LoadedSheet[]>([]);
  /** When editing an existing sheet (clicking its badge), store it here; null = new sheet */
  const [editingSheet, setEditingSheet] = useState<LoadedSheet | null>(null);
  /** Auto-incrementing sheet counter (never decreases, avoids tag reuse) */
  const sheetCounterRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const countryCodeRef = useRef<HTMLInputElement>(null);

  const commitInput = () => {
    if (!input.trim()) return;
    // Handle comma-separated numbers
    const parts = input.split(',').map((s) => s.trim()).filter(Boolean);
    for (const part of parts) {
      // Phone TPAs: normalise to E.164 with the user's default country code.
      // Non-phone TPAs (Intercom, etc.): pass the input through verbatim.
      const value = isPhoneBased ? applyCountryCode(part, defaultCountryCode) : part;
      addManualRecipient(value, value);
    }
    setInput('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitInput();
    }
    if (e.key === 'Backspace' && !input && recipients.length > 0) {
      removeRecipient(recipients[recipients.length - 1].id);
    }
  };

  const handleBlur = () => {
    commitInput();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (text.includes('\n') || text.includes(',')) {
      e.preventDefault();
      const numbers = text.split(/[,\n]/).map((n) => n.trim()).filter(Boolean);
      numbers.forEach((num) => {
        // Same isPhoneBased gate as commitInput — see note there.
        const value = isPhoneBased ? applyCountryCode(num, defaultCountryCode) : num;
        addManualRecipient(value, value);
      });
    }
  };

  /** Open dialog for a NEW sheet upload */
  const openNewSheet = () => {
    setEditingSheet(null);
    setCsvDialogOpen(true);
  };

  /** Open dialog to view/re-import an existing sheet */
  const openExistingSheet = (sheet: LoadedSheet) => {
    setEditingSheet(sheet);
    setCsvDialogOpen(true);
  };

  /** Delete a sheet: remove its recipients and remove from loadedSheets */
  const deleteSheet = (tag: string) => {
    removeRecipientsByTag(tag);
    setLoadedSheets((prev) => {
      const remaining = prev.filter((s) => s.tag !== tag);
      // Reset counter when all sheets are cleared so next upload starts at sheet-1
      if (remaining.length === 0) {
        sheetCounterRef.current = 0;
      }
      return remaining;
    });
  };

  /** Generate the next sheet tag (e.g. "sheet-1", "sheet-2") */
  const getNextSheetTag = () => {
    sheetCounterRef.current += 1;
    return `sheet-${sheetCounterRef.current}`;
  };

  /** Called by CSVImportDialog after successful import */
  const handleSheetImported = (csvText: string, fileName: string, sheetTag: string) => {
    setLoadedSheets((prev) => {
      const existing = prev.findIndex((s) => s.tag === sheetTag);
      const entry: LoadedSheet = { tag: sheetTag, fileName, csvText };
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = entry;
        return updated;
      }
      return [...prev, entry];
    });
  };

  // ── Phone field type selector (CRM context with multiple field types) ──
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);
  const crmModuleFields = useUIStore((s) => s.crmModuleFields);
  const crmSearchRecords = useUIStore((s) => s.crmSearchRecords);
  const crmSearchRecipients = useUIStore((s) => s.crmSearchRecipients);
  const phoneFieldDefs = useMemo(() => (crmModuleFields ?? []).filter(isPhoneFieldDef), [crmModuleFields]);
  // Phone fields of each Search CRM lead, limited to the numbers the user
  // ticked — an unticked number of the same lead must never become a
  // pill or a chip.
  const searchPickedFields = useMemo(() => {
    const ticked = new Map<string, Set<string>>(); // `${module}_${entityId}` → digits
    for (const r of crmSearchRecipients) {
      const key = `${r.module}_${r.entityId}`;
      let set = ticked.get(key);
      if (!set) {
        set = new Set();
        ticked.set(key, set);
      }
      set.add(r.phone.replace(/\D/g, ''));
    }
    return crmSearchRecords.map((record) => ({
      record,
      fields: searchRecordPhoneFields(record, phoneFieldDefs).filter((f) =>
        ticked.get(`${record.module}_${record.id}`)?.has(f.value.replace(/\D/g, ''))
      ),
    }));
  }, [crmSearchRecords, crmSearchRecipients, phoneFieldDefs]);
  const phoneFieldTypes = useMemo(() => {
    // Deduplicate by apiName to get unique field types (e.g. Phone, Mobile, WhatsApp Number)
    const seen = new Map<string, string>();
    if (crmRecordContext) {
      for (const pf of crmPhoneFields) {
        const key = pf.apiName;
        if (!seen.has(key)) {
          // Use base label without record name prefix
          const label = pf.displayLabel.includes(' — ')
            ? pf.displayLabel.split(' — ').pop()!.replace(/ \d+$/, '').trim()
            : pf.displayLabel.replace(/ \d+$/, '').trim();
          seen.set(key, label);
        }
      }
    }
    // Leads added from the Search CRM dialog never reach crmPhoneFields.
    for (const { fields } of searchPickedFields) {
      for (const { apiName } of fields) {
        if (!seen.has(apiName)) {
          seen.set(apiName, phoneFieldDefs.find((f) => f.apiName === apiName)?.displayLabel ?? apiName.replace(/_/g, ' '));
        }
      }
    }
    if (seen.size <= 1) return [];
    return Array.from(seen.entries()).map(([apiName, label]) => ({ apiName, label }));
  }, [crmRecordContext, crmPhoneFields, searchPickedFields, phoneFieldDefs]);

  // All phone field types start selected
  const [activePhoneFields, setActivePhoneFields] = useState<Set<string> | null>(null);

  const knownFieldKeysRef = useRef<Set<string>>(new Set());
  // Numbers missing from the list at the first pill toggle = numbers the
  // user removed by hand. Every toggle rebuilds the CRM chips from the
  // records, so without this a removed number would come back.
  const removedNumbersRef = useRef<Set<string> | null>(null);
  // A chip removed AFTER the first toggle isn't in that snapshot — record it
  // on ×, or the next toggle rebuilds it.
  const removeChip = (id: string) => {
    const chip = recipients.find((r) => r.id === id);
    if (chip && removedNumbersRef.current) removedNumbersRef.current.add(chip.phone.replace(/\D/g, '') || chip.phone);
    removeRecipient(id);
  };

  // Initialize active fields when phoneFieldTypes become available
  const allFieldKeys = useMemo(() => new Set(phoneFieldTypes.map((ft) => ft.apiName)), [phoneFieldTypes]);
  useEffect(() => {
    if (phoneFieldTypes.length > 1 && activePhoneFields === null) {
      setActivePhoneFields(new Set(phoneFieldTypes.map((ft) => ft.apiName)));
      // Fresh mount starts with every pill selected — clear any leftover
      // filter flag so auto-fill can restore recipients a previous mount's
      // filter removed.
      useMessageStore.getState().setRecipientsPillFiltered(false);
    } else if (phoneFieldTypes.length > 1 && activePhoneFields) {
      // A pill that appears later (a lead added from Search CRM) starts
      // selected — its numbers were added unfiltered.
      const added = phoneFieldTypes.filter((ft) => !knownFieldKeysRef.current.has(ft.apiName));
      if (added.length > 0) setActivePhoneFields(new Set([...activePhoneFields, ...added.map((ft) => ft.apiName)]));
    } else if (phoneFieldTypes.length <= 1 && activePhoneFields !== null) {
      // Pills gone (recipients cleared) — start fresh when they come back.
      setActivePhoneFields(null);
      removedNumbersRef.current = null;
      useMessageStore.getState().setRecipientsPillFiltered(false);
    }
    knownFieldKeysRef.current = new Set(phoneFieldTypes.map((ft) => ft.apiName));
  }, [phoneFieldTypes, activePhoneFields]);

  // Drop Search CRM records once no CRM recipient is left (Clear, or the
  // list reset after a send) so their pills don't outlive them.
  const hasCrmRecipients = recipients.some((r) => r.source === 'crm');
  useEffect(() => {
    if (!hasCrmRecipients && crmSearchRecords.length > 0 && !useMessageStore.getState().recipientsPillFiltered) {
      useUIStore.getState().clearCrmSearchRecords();
    }
  }, [hasCrmRecipients, crmSearchRecords]);

  // Compose Reset clears the store's recipients, but the CSV sheet badges,
  // pill selection and typed text live here — clear them too, or a removed
  // CSV still shows after Reset. Skips the mount run. Search CRM leads are
  // left to the "no CRM recipient left" effect above. (KAN-218)
  const resetCount = useMessageStore((s) => s.resetCount);
  const seenResetCountRef = useRef(resetCount);
  useEffect(() => {
    if (resetCount === seenResetCountRef.current) return;
    seenResetCountRef.current = resetCount;
    setLoadedSheets([]);
    setEditingSheet(null);
    sheetCounterRef.current = 0;
    setInput('');
    setActivePhoneFields(null);
    removedNumbersRef.current = null;
  }, [resetCount]);

  /**
   * Per record: which phone fields hold each normalized number
   * (recordId → normalized number → apiNames).
   *
   * A chip is kept only when ITS OWN record has its number under a
   * selected pill. Checking every record instead kept a "(Phone)" chip
   * alive when the Phone pill was off, just because a different record
   * had the same number under a selected field — the chip showed the
   * wrong field and the message was logged on the wrong record.
   */
  const crmRecords = useModuleStore((s) => s.crmRecords);
  const recordPhoneFields = useMemo(() => {
    const map = new Map<string, Map<string, Set<string>>>();
    const schemaFieldNames = new Set(phoneFieldDefs.map((f) => f.apiName));
    for (const rec of crmRecords) {
      if (!rec.data) continue;
      const byNumber = new Map<string, Set<string>>();
      const add = (apiName: string, val: string) => {
        const normalized = val.replace(/\D/g, '');
        if (!normalized) return;
        let set = byNumber.get(normalized);
        if (!set) {
          set = new Set();
          byNumber.set(normalized, set);
        }
        set.add(apiName);
      };
      for (const fd of phoneFieldDefs) {
        let rawVal = rec.data[fd.apiName];
        if (rawVal == null) {
          // Nested dot-path fallback (e.g. "billing.phone"), mirroring
          // extractPhoneFields' resolution.
          const dotIdx = fd.apiName.indexOf('.');
          const nestedPath = dotIdx >= 0 ? fd.apiName.substring(dotIdx + 1) : fd.apiName;
          rawVal = nestedPath.split('.').reduce<unknown>(
            (obj, key) => (obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[key] : undefined),
            rec.data,
          );
        }
        for (const v of resolveRawPhoneValues(rawVal)) add(fd.apiName, v);
      }
      // Synthetic `phone_*` keys injected outside the schema (e.g. a
      // Pipedrive org's number resolved from an associated person).
      for (const key of Object.keys(rec.data)) {
        if (!key.startsWith('phone_') || schemaFieldNames.has(key)) continue;
        for (const v of resolveRawPhoneValues(rec.data[key])) add(key, v);
      }
      map.set(rec.id, byNumber);
    }
    // Leads added from the Search CRM dialog (keyword fallback when the
    // module fields aren't loaded, same as their pills).
    for (const { record: rec, fields } of searchPickedFields) {
      const byNumber = map.get(rec.id) ?? new Map<string, Set<string>>();
      for (const { apiName, value } of fields) {
        const normalized = value.replace(/\D/g, '');
        let set = byNumber.get(normalized);
        if (!set) {
          set = new Set();
          byNumber.set(normalized, set);
        }
        set.add(apiName);
      }
      map.set(rec.id, byNumber);
    }
    return map;
  }, [crmRecords, searchPickedFields, phoneFieldDefs]);

  const handlePhoneFieldToggle = useCallback((apiName: string) => {
    if (!saasModule) return;
    const msgStore = useMessageStore.getState();
    const currentRecipients = msgStore.recipients;

    const prev = activePhoneFields || allFieldKeys;
    const next = new Set(prev);
    if (next.has(apiName)) {
      if (next.size <= 1) return; // Don't deselect the last one
      next.delete(apiName);
    } else {
      next.add(apiName);
    }

    setActivePhoneFields(next);
    // Tell auto-fill effects a pill filter is narrowing the list, so they
    // don't re-add recipients the filter removed (filtering down to zero
    // used to trigger a refill that made the pills look like no-ops).
    msgStore.setRecipientsPillFiltered(next.size !== allFieldKeys.size);

    const numberKey = (phone: string) => phone.replace(/\D/g, '') || phone;

    // Every chip of every record, built one record at a time so no
    // cross-record duplicate is dropped yet. Filter first, dedup after:
    // deduping first left one chip per number tagged with whichever field
    // came first, which the filter could then only keep or drop wholesale.
    const candidates = [
      ...crmRecords.flatMap((rec) => saasModule.addRecordsAsRecipients([rec], tpaModule ?? undefined)),
      ...crmSearchRecipients.map((r) => ({ ...r })),
    ];

    // Chips not rebuilt from the records (manual, CSV) are not
    // pill-controlled — keep them as is. Matched by id, not
    // `source`: some modules emit record chips without `source`/`entityId`.
    const candidateIds = new Set(candidates.map((c) => c.id));
    const untouched = currentRecipients.filter((r) => !candidateIds.has(r.id));

    if (removedNumbersRef.current === null) {
      const present = new Set(currentRecipients.map((r) => numberKey(r.phone)));
      removedNumbersRef.current = new Set(
        candidates.map((c) => numberKey(c.phone)).filter((n) => !present.has(n))
      );
    }
    const removedNumbers = removedNumbersRef.current;
    // A removed number that is back in the list (added again from Search
    // CRM) is wanted again.
    for (const r of currentRecipients) removedNumbers.delete(numberKey(r.phone));

    // Seeded with the kept chips so a number already added by hand / CSV
    // is not added a second time from the records.
    const seen = new Set(untouched.map((r) => numberKey(r.phone)));
    const filtered: Recipient[] = [];
    for (const c of candidates) {
      const key = numberKey(c.phone);
      if (removedNumbers.has(key)) continue;

      const digits = c.phone.replace(/\D/g, '');
      if (!digits) {
        // Non-phone identifier (Intercom contact id etc.) — fall back
        // to the legacy id-suffix check so we don't accidentally drop
        // them from non-phone TPAs.
        if (!Array.from(next).some((field) => c.id.endsWith(`_${field}`))) continue;
      } else {
        const fields = c.entityId ? recordPhoneFields.get(c.entityId)?.get(digits) : undefined;
        // Fields with no pill can't be switched off, so they count as on.
        const onFields = fields ? Array.from(fields).filter((f) => next.has(f) || !allFieldKeys.has(f)) : [];
        // Number not attributable to any field of its record (data shape
        // we couldn't resolve) — keep it. The pills filter between known
        // field types; they must never silently swallow an unknown number.
        if (fields && onFields.length === 0) continue;
        const ownField = fields ? Array.from(fields).find((f) => c.id.endsWith(`_${f}`)) : undefined;
        if (ownField && !onFields.includes(ownField)) {
          // Same number sits in another, selected field of the SAME record
          // (the per-record dedup kept the first field) — relabel the chip
          // to the selected field instead of dropping it. The id stays, so
          // the next toggle still matches this chip to its candidate.
          c.name = c.name.replace(`(${ownField})`, `(${onFields[0]})`);
          c.phoneFieldApiName = onFields[0];
        }
      }

      if (seen.has(key)) continue;
      seen.add(key);
      filtered.push(c);
    }
    msgStore.setRecipients([...untouched, ...filtered]);
  }, [saasModule, tpaModule, crmRecords, crmSearchRecipients, activePhoneFields, allFieldKeys, recordPhoneFields]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-primary">To</Label>
        {count > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground tabular-nums">{count} recipient{count !== 1 ? 's' : ''}</span>
            <button onClick={clearRecipients} className="text-[11px] text-muted-foreground hover:text-destructive transition-colors">Clear</button>
          </div>
        )}
      </div>

      {/* Phone field type selector */}
      {phoneFieldTypes.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {phoneFieldTypes.map((ft) => (
            <button
              key={ft.apiName}
              onClick={() => handlePhoneFieldToggle(ft.apiName)}
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
                activePhoneFields?.has(ft.apiName)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/50 text-muted-foreground border-input hover:bg-muted'
              )}
            >
              <Phone className="h-3 w-3" />
              {ft.label}
            </button>
          ))}
        </div>
      )}

      {/* Google Sheets panel — above the recipient chip list */}
      {appCodeSAAS === 'googlesheets' && (
        <GoogleSheetsPanel saasModule={saasModule} />
      )}

      {/* Progress bar for multi-record loading */}
      {crmRecordContext && crmRecordsLoading && crmRecordsTotal > 1 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Loading contacts...</span>
            <span>{crmRecordsLoaded} / {crmRecordsTotal}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${Math.round((crmRecordsLoaded / crmRecordsTotal) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Chip input */}
      <div
        data-testid="recipient-box"
        className={cn(
          'min-h-[44px] rounded-lg border border-input bg-background p-2 transition-colors',
          'focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20',
          'cursor-text'
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {crmRecordContext && crmRecordsLoading && crmRecordsTotal <= 1 ? (
          <div className="flex items-center gap-2 py-0.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading contact details...</span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1 items-center">
              {recipients.map((r) => (
                <RecipientTag key={r.id} recipient={r} onRemove={removeChip} />
              ))}
              <input
                data-testid="recipient-input"
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onPaste={handlePaste}
                placeholder={recipients.length === 0 ? (isPhoneBased ? 'Phone numbers, comma separated' : 'Contact ID or email, comma separated') : ''}
                className="flex-1 min-w-0 sm:min-w-[160px] bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            {/* Choose which page of list-view records to pull into this box. */}
            <ListPagePicker />
          </>
        )}
      </div>

      {/* Actions row — CSV/CRM buttons */}
      <div className="flex items-center flex-wrap gap-1.5">
          <button
            data-testid="csv-upload-btn"
            onClick={openNewSheet}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <Upload className="h-3 w-3" />
            Add Recipients From CSV
          </button>
          {/* Loaded sheet badges */}
          {loadedSheets.map((sheet) => (
            <span
              key={sheet.tag}
              className="inline-flex items-center gap-1 text-[11px] rounded-full border border-green-200 bg-green-50 text-green-700 pl-2 pr-1 py-0.5 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400"
            >
              <button
                onClick={() => openExistingSheet(sheet)}
                className="inline-flex items-center gap-1 hover:underline"
              >
                <FileSpreadsheet className="h-3 w-3" />
                {sheet.fileName}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteSheet(sheet.tag); }}
                className="rounded-full p-0.5 hover:bg-green-200 dark:hover:bg-green-500/20 transition-colors"
                title="Remove sheet and its recipients"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          {saasModule && appCodeSAAS !== 'spreadsheet' && appCodeSAAS !== 'googlesheets' && (
            <button
              data-testid="search-crm-btn"
              onClick={() => setCrmSearchOpen(true)}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors ml-1"
            >
              <Search className="h-3 w-3" />
              Search {saasName}
            </button>
          )}
          {/* Country code input — right-aligned in the actions row */}
          {isPhoneBased && (
            <div
              className="inline-flex items-center gap-1.5 ml-auto"
              title={preferences.multiCountryContacts ? 'Disabled while "Multi-country contacts" is on (see Compose View settings)' : undefined}
            >
              <span className="text-[11px] text-muted-foreground">Default country code</span>
              <div className="inline-flex items-center rounded border border-dashed border-input px-1.5 py-0.5 focus-within:border-solid focus-within:border-primary">
                <span className="text-[11px] font-medium text-muted-foreground">+</span>
                <input
                  ref={countryCodeRef}
                  data-testid="country-code-input"
                  value={defaultCountryCode.replace(/^\+/, '')}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    updatePreferences({ defaultCountryCode: val });
                  }}
                  disabled={!!preferences.multiCountryContacts}
                  placeholder="91"
                  className="w-8 bg-transparent text-[11px] font-medium outline-none text-foreground placeholder:text-muted-foreground/40 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          )}
        </div>

      {/* KAN-125: shown whenever the To box has something in it (chips or
          text being typed); hidden on an empty form. */}
      {isPhoneBased && preferences.multiCountryContacts && (recipients.length > 0 || input.trim().length > 0) && (
        <MultiCountryWarning onTurnedOff={() => requestAnimationFrame(() => countryCodeRef.current?.focus())} />
      )}

      <CSVImportDialog
        open={csvDialogOpen}
        onOpenChange={setCsvDialogOpen}
        onImport={importFromCSV}
        getNextSheetTag={getNextSheetTag}
        initialData={editingSheet}
        onDataLoaded={handleSheetImported}
        onRemoveOldRecipients={removeRecipientsByTag}
        sheetCount={loadedSheets.length}
      />
      {saasModule && (
        <CRMSearchDialog open={crmSearchOpen} onOpenChange={setCrmSearchOpen} onSearch={searchCRM} onAdd={addFromCRM} modules={saasModule.getSupportedModules()} />
      )}
    </div>
  );
}

/** The phone field in a CRM recipient's id (`crm_<module>_<entityId>_<apiName>`). */
function idPhoneField(r: Recipient): string | null {
  const prefix = `crm_${r.module}_${r.entityId}_`;
  if (!r.id.startsWith(prefix)) return null;
  const apiName = r.id.slice(prefix.length);
  return apiName && apiName !== 'tpa' ? apiName : null;
}

/** The phone field a CRM chip stands for — the pill-switched one first. */
function recipientPhoneField(r: Recipient): string | null {
  return r.phoneFieldApiName ?? idPhoneField(r);
}

/**
 * A CRM recipient's name without its "(apiName)" suffix, plus the label of
 * its phone field ("Mobile") — null when the chip isn't tied to a field.
 */
function recipientNameAndField(r: Recipient, moduleFields: CRMField[]): { name: string; field: string | null } {
  const apiName = recipientPhoneField(r);
  if (!apiName) return { name: r.name, field: null };
  // Strip the suffix the name already carries (the id's field, or the
  // switched field after a pill toggle relabelled it).
  const suffix = [` (${apiName})`, ` (${idPhoneField(r)})`].find((sfx) => r.name.endsWith(sfx));
  const name = suffix ? r.name.slice(0, -suffix.length) : r.name;
  const field = moduleFields.find((f) => f.apiName === apiName)?.displayLabel ?? apiName.replace(/_/g, ' ');
  return { name, field };
}

/**
 * "test2 (Mobile)" for every CRM recipient — addRecordsAsRecipients only
 * suffixes leads with 2+ numbers, so the brackets looked random.
 */
function recipientNameWithField(r: Recipient, moduleFields: CRMField[]): string {
  const { name, field } = recipientNameAndField(r, moduleFields);
  return field ? `${name} (${field})` : name;
}

function RecipientTag({ recipient, onRemove }: { recipient: Recipient; onRemove: (id: string) => void }) {
  const Icon = SOURCE_ICONS[recipient.source] || User;
  const colorClass = SOURCE_COLORS[recipient.source] || SOURCE_COLORS.manual;
  const crmModuleFields = useUIStore((s) => s.crmModuleFields);
  // Modules that set phoneFieldName name the field themselves — unless a
  // pill toggle switched the chip to another field (phoneFieldApiName);
  // the saved label still names the old field then. CSV chips carry
  // their phone column's header instead.
  const phoneFieldLabel = recipient.properties?.phoneFieldName ?? recipient.phoneFieldLabel;
  const { name, field } = phoneFieldLabel && !recipient.phoneFieldApiName
    ? { name: recipient.name, field: String(phoneFieldLabel) }
    : recipientNameAndField(recipient, crmModuleFields ?? []);
  const hasName = !!name && name !== recipient.phone;
  // KAN-208: the field label used to sit inside the truncated name, so a
  // long name cut it off ("Sabari Iyappan (…"). Only the name truncates now;
  // the label is its own tag, so every chip shows Phone / Mobile / WhatsApp.
  const title = [hasName ? name : null, field, recipient.phone].filter(Boolean).join(' · ');

  return (
    <span data-testid="recipient-chip" title={title} className={cn('inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors', colorClass)}>
      <Icon className="h-3 w-3 shrink-0" />
      {hasName && (
        <span data-testid="recipient-chip-name" className="min-w-0 max-w-[140px] truncate">{name}</span>
      )}
      {field && (
        <span data-testid="recipient-chip-field" className="shrink-0 whitespace-nowrap rounded bg-black/5 px-1 text-[10px] font-medium dark:bg-white/10">
          {field}
        </span>
      )}
      <span className={cn('shrink-0 whitespace-nowrap', hasName && 'text-[10px] opacity-60 dark:opacity-80')}>{recipient.phone}</span>
      <button data-testid="recipient-chip-remove" onClick={(e) => { e.stopPropagation(); onRemove(recipient.id); }} className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

function CSVImportDialog({ open, onOpenChange, onImport, getNextSheetTag, initialData, onDataLoaded, onRemoveOldRecipients, sheetCount }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (csv: string, columnMap?: { phone: string; name?: string }, options?: { propertyPrefix?: string; sourceTag?: string }) => number;
  /** Called to generate a new sheet tag (e.g. "sheet-1") for fresh uploads */
  getNextSheetTag: () => string;
  initialData?: LoadedSheet | null;
  onDataLoaded?: (csvText: string, fileName: string, tag: string) => void;
  /** Remove old recipients when re-importing an existing sheet */
  onRemoveOldRecipients?: (tag: string) => void;
  /** Current number of loaded sheets (used to decide if prefix is needed) */
  sheetCount: number;
}) {
  const [csvText, setCsvText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [phoneColumn, setPhoneColumn] = useState('');
  const [nameColumn, setNameColumn] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialDataLoadedRef = useRef(false);
  /** The tag assigned to this dialog session (set on open) */
  const resolvedTagRef = useRef('');

  const hasData = csvText.trim().split('\n').length >= 2 && headers.length > 0;

  // When dialog opens: restore existing sheet or prepare for new sheet
  useEffect(() => {
    if (open) {
      if (initialData && !initialDataLoadedRef.current) {
        // Editing existing sheet — restore its data
        initialDataLoadedRef.current = true;
        resolvedTagRef.current = initialData.tag;
        setFileName(initialData.fileName);
        processCSV(initialData.csvText);
      } else if (!initialData && !initialDataLoadedRef.current) {
        // New sheet — assign next tag
        initialDataLoadedRef.current = true;
        resolvedTagRef.current = getNextSheetTag();
      }
    }
    if (!open) {
      initialDataLoadedRef.current = false;
      // Reset dialog state when closed
      resetState();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Process CSV text — extract headers and auto-detect columns
  const processCSV = useCallback((text: string) => {
    setCsvText(text);
    setError(null);
    setImportedCount(null);

    const extracted = extractCSVHeaders(text);
    setHeaders(extracted);

    if (extracted.length > 0) {
      const detected = autoDetectColumns(extracted);
      setPhoneColumn(detected.phone || '');
      setNameColumn(detected.name || '');
    } else {
      setPhoneColumn('');
      setNameColumn('');
    }
  }, []);

  const resetState = useCallback(() => {
    setCsvText('');
    setHeaders([]);
    setPhoneColumn('');
    setNameColumn('');
    setFileName(null);
    setError(null);
    setImportedCount(null);
  }, []);

  const handleImport = () => {
    try {
      setError(null);
      if (!phoneColumn) {
        setError('Please select the phone number column');
        return;
      }
      const tag = resolvedTagRef.current;

      // If re-importing an existing sheet, remove old recipients first
      if (initialData && tag) {
        onRemoveOldRecipients?.(tag);
      }

      // Skip property prefix for single-sheet mode (spreadsheet apps) — columns are just "name", "mobile", etc.
      // Only add prefix when multiple sheets exist (to disambiguate columns across sheets)
      const isReimport = !!initialData;
      const needsPrefix = isReimport ? sheetCount > 1 : sheetCount > 0;
      const count = onImport(
        csvText,
        { phone: phoneColumn, name: nameColumn || undefined },
        { propertyPrefix: needsPrefix ? tag : undefined, sourceTag: tag },
      );
      setImportedCount(count);
      if (count > 0) {
        // Notify parent to persist the loaded file data
        if (fileName) onDataLoaded?.(csvText, fileName, tag);
        // Close dialog
        setTimeout(() => { onOpenChange(false); }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (ext === '.xlsx' || ext === '.xls') {
      // Excel files: read as binary, convert to CSV using SheetJS (matches old code excelFileToJSON)
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = ev.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          // Use first sheet (old code: result.Sheet1 || first key)
          const sheetName = workbook.SheetNames[0];
          if (sheetName) {
            // Use sheet_to_json (old code: sheet_to_row_object_array) to get JS objects
            // with raw number values — avoids scientific notation in CSV conversion.
            const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
              workbook.Sheets[sheetName],
              { defval: '' },
            );
            if (rows.length > 0) {
              const hdrs = Object.keys(rows[0]);
              // Rebuild as CSV with all values stringified (preserves phone numbers)
              const csvLines = [hdrs.join(',')];
              for (const row of rows) {
                csvLines.push(
                  hdrs.map((h) => {
                    const v = row[h];
                    // Convert numbers to full-precision string (no scientific notation)
                    const s = typeof v === 'number'
                      ? (Number.isInteger(v) ? v.toString() : v.toFixed(0))
                      : String(v ?? '');
                    // Escape CSV values that contain commas or quotes
                    return s.includes(',') || s.includes('"') || s.includes('\n')
                      ? `"${s.replace(/"/g, '""')}"`
                      : s;
                  }).join(','),
                );
              }
              setFileName(file.name);
              processCSV(csvLines.join('\n'));
            }
          }
        } catch (err) {
          setError('Failed to read Excel file. Please try a .csv format.');
          console.error('[CSVImport] Excel parse error:', err);
        }
      };
      reader.readAsBinaryString(file);
    } else {
      // CSV/TSV/TXT: read as text
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFileName(file.name);
        processCSV(ev.target?.result as string);
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  // Preview first few data rows
  const previewRows = useMemo(() => {
    if (!csvText || headers.length === 0) return [];
    const lines = csvText.trim().split('\n');
    return lines.slice(1, 5).map((line) => parseCSVLine(line).map((v) => v.trim()));
  }, [csvText, headers]);

  const tag = resolvedTagRef.current;
  const sheetLabel = tag ? tag.replace('sheet-', 'Sheet ') : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>
            Import Recipients from CSV / Excel
            {sheetLabel && <span className="ml-2 text-sm font-normal text-muted-foreground">({sheetLabel})</span>}
          </DialogTitle>
          <DialogDescription>
            Upload a CSV/Excel file or paste content with phone numbers.
            {tag && <> Placeholders will be prefixed with <code className="text-xs bg-muted px-1 rounded">{tag}-</code></>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {/* File upload */}
          <div>
            <input ref={fileInputRef} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={handleFileUpload} className="hidden" />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
              <Upload className="h-4 w-4" /> Upload CSV/Excel File
            </Button>
          </div>

          {/* Paste area — shown only before data is loaded */}
          {!hasData && (
            <>
              <div className="text-center text-xs text-muted-foreground">or paste CSV below</div>
              <textarea
                value={csvText}
                onChange={(e) => processCSV(e.target.value)}
                placeholder={'name,phone\nJohn Doe,+1234567890\nJane Smith,+0987654321'}
                rows={6}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </>
          )}

          {/* Column mapping — shown after CSV is loaded */}
          {hasData && (
            <div className="space-y-3">
              {/* File name display (matches old UI popup) */}
              {fileName && (
                <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-600 shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">{fileName}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium">
                    Phone Column <span className="text-destructive">*</span>
                  </Label>
                  <select
                    value={phoneColumn}
                    onChange={(e) => setPhoneColumn(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select...</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium">Name Column</Label>
                  <select
                    value={nameColumn}
                    onChange={(e) => setNameColumn(e.target.value)}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">None</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Data preview */}
              {previewRows.length > 0 && (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-muted">
                      <tr>
                        {headers.slice(0, 4).map((h) => (
                          <th key={h} className="px-2 py-1.5 text-left font-medium text-muted-foreground truncate max-w-[100px]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {previewRows.map((row, i) => (
                        <tr key={i}>
                          {headers.slice(0, 4).map((_, idx) => (
                            <td key={idx} className="px-2 py-1 truncate max-w-[100px] text-muted-foreground">{row[idx] || ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Change file */}
              <button
                onClick={() => { resetState(); }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-3 w-3" /> Change file
              </button>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
          {importedCount !== null && (
            <div className="text-sm text-success font-medium">Successfully imported {importedCount} recipients</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={!csvText.trim() || !phoneColumn}>
              <Users className="h-4 w-4" /> Import
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CRMSearchDialog({ open, onOpenChange, onSearch, onAdd, modules }: {
  open: boolean; onOpenChange: (open: boolean) => void;
  onSearch: (module: string, query: string) => Promise<Recipient[]>;
  onAdd: (recipients: Recipient[]) => void;
  modules: Array<{ value: string; label: string; selected: boolean }>;
}) {
  const [query, setQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState(modules.find((m) => m.selected)?.value || modules[0]?.value || 'Contacts');
  const [results, setResults] = useState<Recipient[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Every row names its phone field, same as the To chips.
  const crmModuleFields = useUIStore((s) => s.crmModuleFields);
  const rowName = useCallback((r: Recipient) => recipientNameWithField(r, crmModuleFields ?? []), [crmModuleFields]);

  // Row id → name of the earlier lead with the same number. Both rows are
  // listed so no lead looks missing; only one message goes to the number.
  const sameNumberAs = useMemo(() => {
    const firstByNumber = new Map<string, Recipient>();
    const notes = new Map<string, string>();
    for (const r of results) {
      const digits = r.phone.replace(/\D/g, '');
      if (!digits) continue;
      const first = firstByNumber.get(digits);
      if (!first) firstByNumber.set(digits, r);
      else if (first.entityId !== r.entityId) notes.set(r.id, rowName(first));
    }
    return notes;
  }, [results, rowName]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try { 
      const found = await onSearch(selectedModule, query.trim());
      setResults(found);
      setSelected(new Set());
    }
    catch { 
      setResults([]); 

    }
    finally { 
      setSearching(false); 

    }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const handleAdd = () => {
    onAdd(results.filter((r) => selected.has(r.id)));
    onOpenChange(false); setQuery(''); setResults([]); setSelected(new Set());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="search-crm-dialog">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Search Contacts</DialogTitle>
          <DialogDescription>Search for contacts to add as recipients.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="flex gap-2">
            <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)} className="shrink-0 rounded-lg border border-input bg-background px-2 py-2 text-sm">
              {modules.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
            </select>
            <div className="flex flex-1 min-w-0 gap-2">
              <input data-testid="search-crm-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} placeholder="Search by name, phone, email..." className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
              <Button data-testid="search-crm-search-btn" onClick={handleSearch} disabled={searching || !query.trim()} size="sm">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border scrollbar-thin">
            {results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">{searching ? 'Searching...' : 'Search for contacts above'}</p>
              </div>
            ) : (
              results.map((r) => (
                <label key={r.id} data-testid="search-crm-result" className={cn('flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors border-b border-border last:border-b-0', selected.has(r.id) && 'bg-primary/5')}>
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} className="rounded border-input" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate">{rowName(r)}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {r.phone}
                      {r.module && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{r.module}</Badge>}
                    </span>
                    {sameNumberAs.has(r.id) && (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400">
                        Same number as {sameNumberAs.get(r.id)} · gets one message
                      </span>
                    )}
                  </div>
                </label>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="search-crm-add-btn" onClick={handleAdd} disabled={selected.size === 0}>
              <Plus className="h-4 w-4" /> Add {selected.size} Recipient{selected.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Google Sheets Recipient Panel ──────────────────────────────────────

/** Duck-typed interface for GoogleSheetsModule (avoids direct import, keeps dynamic loading) */
interface GoogleSheetsLike {
  fetchSheetData(type: 'all' | 'selected'): void;
  setNameColumn(col: string): void;
  setPhoneColumn(col: string): void;
  setStatusColumn(col: string): void;
  setMsgIdColumn(col: string): void;
  setSpreadsheetIdFromInput(input: string): boolean;
  getSpreadsheetId(): string;
  fetchSpreadsheetTitle(): Promise<string>;
  canWriteStatusToSheet(): boolean;
  getColumnOptions(): Array<{ label: string; value: string }>;
  csvHeaders: string[];
  csvRows: string[][];
  dataReceivedFromSheet: boolean;
  fetchRecords(module: string, entityId: string): Promise<CRMRecord[]>;
  addRecordsAsRecipients(records: CRMRecord[], tpaModule?: unknown): Recipient[];
}

interface CRMRecord {
  id: string;
  module: string;
  displayName: string;
  phone?: string;
  email?: string;
  data: Record<string, unknown>;
}

/**
 * Label a sheet column the way the user sees it in Google Sheets — A, B, C… —
 * rather than the generated `Column_2_Ex_<value>` key. The generated key stays
 * the option's value, so saved template placeholders keep resolving.
 */
function sheetColumnLabel(header: string, index: number): string {
  let letter = '';
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    n = Math.floor((n - 1) / 26);
  }
  const sample = header.includes('_Ex_') ? header.split('_Ex_').slice(1).join('_Ex_') : '';
  return sample ? `Column ${letter} — ${sample}` : `Column ${letter}`;
}

function GoogleSheetsPanel({ saasModule }: { saasModule: { getSupportedModules(): unknown[] } | null }) {
  const gsModule = saasModule as unknown as GoogleSheetsLike | null;
  const addRecipients = useMessageStore((s) => s.addRecipients);
  const clearRecipients = useMessageStore((s) => s.clearRecipients);

  const [fetchType, setFetchType] = useState<'all' | 'selected'>('all');
  const [nameColumn, setNameColumn] = useState('');
  const [phoneColumn, setPhoneColumn] = useState('');
  const [statusColumn, setStatusColumn] = useState('');
  const [msgIdColumn, setMsgIdColumn] = useState('');
  const [sheetLink, setSheetLink] = useState('');
  const [sheetLinkOk, setSheetLinkOk] = useState(false);
  const [sheetTitle, setSheetTitle] = useState('');
  const [editingSheetLink, setEditingSheetLink] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [hasData, setHasData] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [addedCount, setAddedCount] = useState<number | null>(null);

  // On mount: check if data already arrived (race condition: postMessage can arrive before panel mounts)
  useEffect(() => {
    if (gsModule?.dataReceivedFromSheet && gsModule.csvHeaders.length > 0) {
      setHeaders(gsModule.csvHeaders);
      setRowCount(gsModule.csvRows.length);
      setHasData(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for sheet data arriving via postMessage → custom event from GoogleSheetsModule
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        setHeaders(detail.headers || []);
        setRowCount(detail.rowCount || 0);
        setHasData(detail.hasData ?? false);
        setFetching(false);
        // Reset column selections when new data arrives
        setNameColumn('');
        setPhoneColumn('');
        setStatusColumn('');
        setMsgIdColumn('');
        gsModule?.setStatusColumn('');
        gsModule?.setMsgIdColumn('');
        setAddedCount(null);
      }
    };
    window.addEventListener('googlesheets:data-received', handler);
    return () => window.removeEventListener('googlesheets:data-received', handler);
  }, []);

  /**
   * Trigger fetch when radio button is selected — old code: clicking the radio
   * immediately calls fetchSheetData(type) which posts message to parent.
   * When new data arrives, the event handler resets columns/rows.
   */
  const handleFetchTypeChange = (type: 'all' | 'selected') => {
    if (!gsModule) return;
    setFetchType(type);
    setFetching(true);
    setAddedCount(null);
    // Reset columns immediately (old code: applyCSVValues clears csvMeta + dropdowns)
    setNameColumn('');
    setPhoneColumn('');
    setStatusColumn('');
    setMsgIdColumn('');
    gsModule.setNameColumn('');
    gsModule.setPhoneColumn('');
    gsModule.setStatusColumn('');
    gsModule.setMsgIdColumn('');
    // Clear existing recipients when re-fetching (old code: removeAllRecipeientsinUI)
    clearRecipients();
    gsModule.fetchSheetData(type);
  };

  const handleNameColumnChange = (value: string) => {
    setNameColumn(value);
    gsModule?.setNameColumn(value);
  };

  const handlePhoneColumnChange = (value: string) => {
    setPhoneColumn(value);
    gsModule?.setPhoneColumn(value);
  };

  const handleStatusColumnChange = (value: string) => {
    setStatusColumn(value);
    gsModule?.setStatusColumn(value);
  };

  const handleMsgIdColumnChange = (value: string) => {
    setMsgIdColumn(value);
    gsModule?.setMsgIdColumn(value);
  };

  /** Column choices for our searchable Select — A, B, C… with the sample value. */
  const columnOptions = useMemo(
    () => headers.map((h, i) => ({ value: h, label: sheetColumnLabel(h, i) })),
    [headers],
  );

  const handleSheetLinkChange = (value: string) => {
    setSheetLink(value);
    const ok = gsModule?.setSpreadsheetIdFromInput(value) ?? false;
    setSheetLinkOk(ok);
    setSheetTitle('');
    if (ok) {
      gsModule?.fetchSpreadsheetTitle().then((title) => {
        setSheetTitle(title);
        if (title) setEditingSheetLink(false);
      }).catch(() => {});
    }
  };

  const handleAdd = async () => {
    if (!gsModule || !phoneColumn) return;
    try {
      const records = await gsModule.fetchRecords('Sheets', 'sheet-data');
      const recipients = gsModule.addRecordsAsRecipients(records);
      if (recipients.length > 0) {
        addRecipients(recipients);
        setAddedCount(recipients.length);
      } else {
        setAddedCount(0);
      }
    } catch (err) {
      console.warn('[GoogleSheetsPanel] Error adding recipients:', err);
      setAddedCount(0);
    }
  };

  const handleClear = () => {
    clearRecipients();
    setAddedCount(null);
  };

  return (
    <div data-testid="gsheets-panel" className="rounded-lg border border-input bg-muted/30 p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <FileSpreadsheet className="h-4 w-4 text-green-600" />
        <span data-testid="gsheets-heading" className="text-xs font-semibold text-foreground">Add Recipients From Sheets™</span>
      </div>

      {/* Fetch type radios — selecting triggers fetch immediately (old code behavior) */}
      <div className="flex items-center gap-4">
        <label className={cn('flex items-center gap-1.5 cursor-pointer', fetching && 'opacity-50 pointer-events-none')}>
          <input
            data-testid="gsheets-fetch-all"
            type="radio"
            name="gs-fetch-type"
            checked={fetchType === 'all'}
            onChange={() => handleFetchTypeChange('all')}
            disabled={fetching || !gsModule}
            className="h-3.5 w-3.5 accent-primary"
          />
          <span className="text-xs">Fetch All Data</span>
        </label>
        <label className={cn('flex items-center gap-1.5 cursor-pointer', fetching && 'opacity-50 pointer-events-none')}>
          <input
            data-testid="gsheets-fetch-selected"
            type="radio"
            name="gs-fetch-type"
            checked={fetchType === 'selected'}
            onChange={() => handleFetchTypeChange('selected')}
            disabled={fetching || !gsModule}
            className="h-3.5 w-3.5 accent-primary"
          />
          <span className="text-xs">Fetch Selected</span>
        </label>
        {fetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-auto" />}
      </div>

      {/* Column mapping (shown after data arrives) */}
      {hasData && headers.length > 0 && (
        <>
          <div className="space-y-2">
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground font-medium">Recipient Name column</label>
              <Select
                  data-testid="gsheets-name-column"
                  value={nameColumn}
                  onValueChange={handleNameColumnChange}
                  options={columnOptions}
                  placeholder="Select Column..."
                  searchable
                  emptyText="No columns"
                  triggerClassName="h-8 text-xs"
                />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground font-medium">Phone number column</label>
              <Select
                  data-testid="gsheets-phone-column"
                  value={phoneColumn}
                  onValueChange={handlePhoneColumnChange}
                  options={columnOptions}
                  placeholder="Select Column..."
                  searchable
                  emptyText="No columns"
                  triggerClassName="h-8 text-xs"
                />
            </div>

            {/* Optional: write delivery status back into the sheet. Only offered
                when the add-on gave us a spreadsheet id to write to. */}
            {phoneColumn && (
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground font-medium">
                  Google Sheet link <span className="text-muted-foreground/70">(to write status back)</span>
                </label>
                {sheetTitle && !editingSheetLink ? (
                  <div className="flex items-center gap-2 h-8 rounded-md border border-input bg-muted/40 px-2">
                    <span className="flex-1 min-w-0 truncate text-xs text-foreground">{sheetTitle}</span>
                    <button
                      type="button"
                      data-testid="gsheets-sheet-link-change"
                      onClick={() => setEditingSheetLink(true)}
                      title="Use a different sheet"
                      className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      data-testid="gsheets-sheet-link"
                      type="text"
                      value={sheetLink}
                      autoFocus={editingSheetLink}
                      onChange={(e) => handleSheetLinkChange(e.target.value)}
                      placeholder="Paste this sheet's link from the browser address bar"
                      className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
                    />
                    {sheetLink && !sheetLinkOk && (
                      <p className="text-[10px] text-red-500">
                        That doesn't look like a Google Sheet link.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {gsModule?.canWriteStatusToSheet?.() && (
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground font-medium">
                  Status column <span className="text-muted-foreground/70">(optional)</span>
                </label>
                <Select
                  data-testid="gsheets-status-column"
                  value={statusColumn}
                  onValueChange={handleStatusColumnChange}
                  options={columnOptions}
                  placeholder="Don't write status"
                  searchable
                  emptyText="No columns"
                  triggerClassName="h-8 text-xs"
                />
                <p className="text-[10px] text-muted-foreground">
                  Delivery status is written into this column for each recipient.
                </p>
              </div>
            )}

            {gsModule?.canWriteStatusToSheet?.() && statusColumn && (
              <div className="space-y-1">
                <label className="text-[11px] text-muted-foreground font-medium">
                  Message ID column <span className="text-muted-foreground/70">(recommended)</span>
                </label>
                <Select
                  data-testid="gsheets-msgid-column"
                  value={msgIdColumn}
                  onValueChange={handleMsgIdColumnChange}
                  options={columnOptions}
                  placeholder="Match by phone number only"
                  searchable
                  emptyText="No columns"
                  triggerClassName="h-8 text-xs"
                />
                <p className="text-[10px] text-muted-foreground">
                  Keeps rows correct when two people share a phone number. You can hide this column.
                </p>
              </div>
            )}
          </div>

          {/* Add / Clear buttons */}
          <div className="flex items-center gap-2">
            <Button
              data-testid="gsheets-add-btn"
              size="sm"
              className="h-7 text-xs px-3"
              onClick={handleAdd}
              disabled={!phoneColumn}
            >
              Add
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs px-3"
              onClick={handleClear}
            >
              Clear
            </Button>

            {/* Row count / feedback */}
            <span data-testid="gsheets-row-info" className="text-[11px] text-muted-foreground ml-auto">
              {addedCount !== null
                ? addedCount > 0
                  ? `✓ ${addedCount} recipient${addedCount !== 1 ? 's' : ''} added`
                  : 'No valid phone numbers found'
                : `${rowCount} row${rowCount !== 1 ? 's' : ''} loaded`
              }
            </span>
          </div>
        </>
      )}

      {/* Status messages */}
      {fetching && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Waiting for sheet data...
        </div>
      )}
      {!hasData && !fetching && (
        <p className="text-[11px] text-muted-foreground" data-testid="gsheets-no-recipients">
          No recipients selected
        </p>
      )}
    </div>
  );
}
